import { useEffect } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { logout, selectUser, userRefreshed } from "../redux/slices/userSlice";
import { getTokenExpiry, isSessionExpired } from "../utils/session";

const CHECK_EVERY_MS = 60 * 1000;

// Keeps the dashboard session honest:
// - signs out as soon as the access token expires (also after the dashboard
//   was closed and opened again later)
// - reloads the logged-in user so role changes apply without signing in again
// - signs out if the user was deleted
function SessionGuard() {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectUser);
  const isLoggedIn = currentUser?.isLoggedIn;
  const token = currentUser?.user?.[0]?.[0];
  const userId = currentUser?.user?.[0]?.[1];

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const session = { user: [[token, userId]] };

    const check = async () => {
      if (isSessionExpired(session)) {
        dispatch(logout());
        return;
      }
      try {
        const response = await axios.get(
          `${"https://api.sedihisham.com"}/users/getoneuser/${userId}`
        );
        dispatch(userRefreshed(response.data));
      } catch (err) {
        const status = err.response?.status;
        // 403 = "user not exist"
        if (status === 401 || status === 403 || status === 404) {
          dispatch(logout());
        }
      }
    };

    check();
    const onVisible = () => {
      if (document.visibilityState !== "hidden") check();
    };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    const interval = setInterval(check, CHECK_EVERY_MS);

    // sign out exactly when the token expires
    const expiry = getTokenExpiry(session);
    const expiryTimer =
      expiry && expiry - Date.now() < 2147483647
        ? setTimeout(() => dispatch(logout()), Math.max(0, expiry - Date.now()))
        : null;

    return () => {
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
      if (expiryTimer) clearTimeout(expiryTimer);
    };
  }, [isLoggedIn, token, userId, dispatch]);

  return null;
}

export default SessionGuard;
